import React, { useEffect } from 'react';
import { Box, Grid, TextField, Typography, Divider } from '@mui/material';
import { getCorporateUser, getAllCorporateUsers } from '../CorporateQueries';

/**
 * AnnualReviewHeader
 * Shared header section for Annual Review forms.
 * Displays title, review period, instructions, and Employee Details.
 *
 * Props:
 * - details: { employeeName, title, manager, date }
 * - setDetails: function to update details state
 * - editable: boolean (controls field enabled state)
 * - autoPrefill: boolean (prefill details from corporate user profile)
 * - showEmployeeNote: boolean (show "This section to be completed by the employee.")
 */
const AnnualReviewHeader = ({ details, setDetails, editable = true, autoPrefill = true, showEmployeeNote = true }) => {
  useEffect(() => {
    const loadUserDetails = async () => {
      try {
        if (!autoPrefill) return;
        // 1) Prefer data cached at login
        try {
          const cached = localStorage.getItem('corporateUser');
          if (cached) {
            const u = JSON.parse(cached);
            const first = u.first_name || '';
            const last = u.last_name || '';
            const title = u.job_title || '';
            const manager = u.reporting_l1 || '';
            if (first || last || title || manager) {
              setDetails(prev => ({
                ...prev,
                employeeName: `${first} ${last}`.trim(),
                title,
                manager
              }));
              return; // Done
            }
          }
        } catch (_) { /* ignore cache parse errors */ }

        // 2) Fallback to fetching by current user email
        let userEmail = localStorage.getItem('user') || '';
        // If user is stored as JSON string, extract email
        try {
          const parsed = JSON.parse(userEmail);
          if (parsed && typeof parsed === 'object' && parsed.email) {
            userEmail = parsed.email;
          }
        } catch (_) { /* not JSON */ }
        userEmail = String(userEmail || '').replace(/^"|"$/g, '').trim();
        if (!userEmail) return;

        const user = await getCorporateUser(userEmail);

        const extract = (u) => {
          const raw = Array.isArray(u?.users) ? u.users[0] : (u?.user || u?.Item || u);
          const val = (obj, key) => {
            const v = obj?.[key];
            if (v && typeof v === 'object' && 'S' in v) return v.S || '';
            return v || '';
          };
          const first = val(raw, 'first_name');
          const last = val(raw, 'last_name');
          const title = val(raw, 'job_title');
          const manager = val(raw, 'reporting_l1');
          return { first, last, title, manager };
        };

        let filled = false;
        if (user) {
          const { first, last, title, manager } = extract(user);
          if (first || last || title || manager) {
            setDetails(prev => ({
              ...prev,
              employeeName: `${first} ${last}`.trim(),
              title,
              manager
            }));
            // Cache minimal corporateUser for instant subsequent loads
            try {
              localStorage.setItem('corporateUser', JSON.stringify({
                email: userEmail,
                first_name: first,
                last_name: last,
                job_title: title,
                reporting_l1: manager
              }));
            } catch(_) {}
            filled = true;
          }
        }

        // 3) Fallback: search all users if direct lookup failed or returned empty
        if (!filled) {
          try {
            const all = await getAllCorporateUsers();
            const match = all.find(a => String(a.email || '').trim().toLowerCase() === userEmail.toLowerCase());
            if (match) {
              setDetails(prev => ({
                ...prev,
                employeeName: `${match.first_name || ''} ${match.last_name || ''}`.trim(),
                title: match.job_title || '',
                manager: match.reporting_l1 || ''
              }));
              try {
                localStorage.setItem('corporateUser', JSON.stringify({
                  email: match.email || userEmail,
                  first_name: match.first_name || '',
                  last_name: match.last_name || '',
                  job_title: match.job_title || '',
                  reporting_l1: match.reporting_l1 || ''
                }));
              } catch(_) {}
            }
          } catch (e) {
            // ignore fallback errors
          }
        }
      } catch (err) {
        console.error('[AnnualReviewHeader] Error loading employee details:', err);
      }
    };
    loadUserDetails();
  }, [autoPrefill, setDetails]);

  const handleChange = (e) => {
    setDetails(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="h6" align="center" sx={{ fontWeight: 700 }}>Annual Performance Review</Typography>
      <Typography variant="subtitle2" align="center" sx={{ fontStyle: 'italic', color: 'text.secondary', mb: 2 }}>
        Review Period April 1st, 2025 - March 31st, 2026
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        The following form should be used to complete the annual performance review. It requires feedback from both the employee and the manager to complete.
      </Typography>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Employee Details</Typography>
      <Divider sx={{ mb: 1 }} />
      {showEmployeeNote && (
        <Typography variant="caption" sx={{ color: 'text.secondary', mb: 2, display: 'block' }}>
          This section to be completed by the employee.
        </Typography>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <TextField label="Employee Name" name="employeeName" value={details.employeeName} onChange={handleChange} fullWidth disabled={!editable} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField label="Title" name="title" value={details.title} onChange={handleChange} fullWidth disabled={!editable} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField label="Manager" name="manager" value={details.manager} onChange={handleChange} fullWidth disabled={!editable} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField label="Date" name="date" type="date" value={details.date} onChange={handleChange} fullWidth InputLabelProps={{ shrink: true }} disabled={!editable} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default AnnualReviewHeader;
